import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Button, Spinner, Alert, Badge } from "react-bootstrap";
import { FaEdit, FaPrint, FaArrowLeft, FaFileInvoice } from "react-icons/fa";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../services/api";
import "./AddPurchase.css";

const ViewInvoice = () => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoiceData();
  }, [id]);

  const fetchInvoiceData = async () => {
    try {
      setLoading(true);
      setError("");
      
      const response = await api.post("/invoice_detail.php", { id });
      
      if (response.data.head.code === 200) {
        setInvoice(response.data.body);
      } else {
        setError(response.data.head.msg || "Invoice not found");
        setInvoice(null);
      }
    } catch (err) {
      console.error("Error fetching invoice:", err);
      setError("Failed to load invoice. Please try again.");
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  };

const handlePrint = () => {
    window.open(`/invoiceprint/${invoice.id}`, '_blank');
  };


  const getStatusVariant = (status) => {
    switch(status) {
      case 'Paid': return 'success';
      case 'Pending': return 'warning';
      case 'Overdue': return 'danger';
      case 'Generated': return 'info';
      case 'Cancelled': return 'secondary';
      default: return 'secondary';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "₹0.00";
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading invoice...</p>
        </div>
      </Container>
    );
  }

  if (error || !invoice) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Invoice Details</h4>
          <Button className="btn-top" onClick={() => navigate("/invoice")}>
            <FaArrowLeft className="me-2" /> Back to List
          </Button>
        </div>
        <Alert variant="danger">
          {error || "Invoice not found."}
        </Alert>
        <div className="text-center mt-4">
          <Button variant="primary" onClick={() => navigate("/invoicet")}>
            Go to Invoice List
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Invoice Details</h4>
        <div className="d-flex gap-2">
          <Button className="btn-submit" onClick={handlePrint}>
            <FaPrint className="me-2" /> Print
          </Button>
          {/* <Link
            to={`/editinvoice/${invoice.id}`}
            className="btn-submit d-inline-flex align-items-center"
          >
            <FaEdit className="me-2" /> Edit
          </Link> */}
          <Button className="btn-top" onClick={() => navigate("/invoice")}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
        </div>
      </div>

      {/* Invoice ID, Date and Status Section */}
      <div className="p-4 border rounded bg-light mb-4">
        <Row>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>Invoice ID:</strong>
              <div className="fs-5 text-primary d-flex align-items-center">
                <FaFileInvoice className="me-2" />
                {invoice.invoice_no || `${invoice.id}`}
              </div>
            </div>
          </Col>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>Date:</strong>
              <div className="fs-5">{invoice.date}</div>
            </div>
          </Col>
          <Col lg={4} md={4} sm={12}>
            {/* <div className="mb-3">
              <strong>Status:</strong>
              <div>
                <Badge bg={getStatusVariant(invoice.status)} className="fs-6">
                  {invoice.status || "Generated"}
                </Badge>
              </div>
            </div> */}
          </Col>
          {invoice.proforma_id && (
            <Col lg={4} md={4} sm={12}>
              <div className="mb-3">
                <strong>Proforma:</strong>
                <div className="fs-6">PRO-{invoice.proforma_id}</div>
              </div>
            </Col>
          )}
          {invoice.quotation_id && (
            <Col lg={4} md={4} sm={12}>
              <div className="mb-3">
                <strong>Quotation ID:</strong>
                <div className="fs-6">{invoice.quotation_id}</div>
              </div>
            </Col>
          )}
        </Row>
      </div>

      {/* Customer Details Section */}
      <div className="p-4 border rounded bg-light mb-4">
        <h5 className="mb-3">Customer Details</h5>
        <Row>
          <Col lg={6} md={6} sm={12}>
            <div className="mb-3">
              <strong>Customer Name:</strong>
              <div className="fs-5">{invoice.customer_name || "N/A"}</div>
            </div>
          </Col>
          <Col lg={6} md={6} sm={12}>
            <div className="mb-3">
              <strong>Phone Number:</strong>
              <div className="fs-5">{invoice.customer_phone || "N/A"}</div>
            </div>
          </Col>
          <Col lg={6} md={6} sm={12}>
            <div className="mb-3">
              <strong>GST Number:</strong>
              <div className="fs-6">{invoice.customer_gst_no || "N/A"}</div>
            </div>
          </Col>
          <Col lg={6} md={6} sm={12}>
            <div className="mb-3">
              <strong>Address:</strong>
              <div className="fs-6">
                {invoice.customer_address || "N/A"} {invoice.customer_city && `, ${invoice.customer_city}`}
                {invoice.customer_state && `, ${invoice.customer_state}`}
                {invoice.customer_pin_code && ` - ${invoice.customer_pin_code}`}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Invoice Items Table */}
      <div className="table-responsive mb-4">
        <Table bordered className="purchase-table">
          <thead className="table-dark">
            <tr>
              <th width="5%" className="text-center">S.No</th>
              {/* <th width="15%">Product Code</th> */}
              <th width="25%">Item</th>
              <th width="15%">Selling Rate (₹)</th>
              <th width="10%">Qty</th>
              <th width="15%" className="text-end">Total (₹)</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items && invoice.items.map((item, index) => (
              <tr key={index}>
                <td className="text-center">{index + 1}</td>
                {/* <td>{item.product_code}</td> */}
                <td>{item.product_name}</td>
                <td>{formatCurrency(item.selling_rate)}</td>
                <td>{item.quantity}</td>
                <td className="text-end">{formatCurrency(item.total)}</td>
              </tr>
            ))}
            {(!invoice.items || invoice.items.length === 0) && (
              <tr>
                <td colSpan="6" className="text-center text-muted py-3">
                  No items found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Totals Section */}
      <Row className="mt-4">
        <Col xs={12} className="d-flex justify-content-end">
          <div className="totals-section">
            <div className="total-row">
              <span className="total-label">Subtotal:</span>
              <span className="total-value">{formatCurrency(invoice.sub_total)}</span>
            </div>
            <div className="total-row">
              <span className="total-label">
                GST ({invoice.gst_percentage}%)
              </span>
              <span className="total-value">{formatCurrency(invoice.gst_amount)}</span>
            </div>
            {/* <div className="total-row">
              <span className="total-label">Package Charges:</span>
              <span className="total-value">{formatCurrency(invoice.package_charges || 0)}</span>
            </div> */}
            <div className="total-row grand-total">
              <span className="total-label">Grand Total:</span>
              <span className="total-value">{formatCurrency(invoice.grand_total)}</span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Notes Section */}
      {invoice.notes && (
        <div className="p-4 border rounded bg-light mt-4">
          <h5>Notes</h5>
          <p>{invoice.notes}</p>
        </div>
      )}

      {/* Action Buttons 
      <div className="d-flex justify-content-end mt-4 gap-2">
        <Button className="btn-top" onClick={handlePrint}>
          <FaPrint className="me-2" /> Print Invoice
        </Button>
        <Link
          to={`/editinvoice/${invoice.id}`}
          className="btn-submit d-inline-flex align-items-center"
        >
          <FaEdit className="me-2" /> Edit Invoice
        </Link>
        <Button className="btn-top" onClick={() => navigate("/invoice")}>
          <FaArrowLeft className="me-2" /> Back to List
        </Button>
      </div>
      */}

      {/* Footer */}
      <div className="text-center mt-5 pt-3 border-top">
        <small className="text-muted">
          Created: {new Date(invoice.created_at).toLocaleString()} | 
          Copyright © Sri Kumaran Traders
        </small>
      </div>
    </Container>
  );
};

export default ViewInvoice;